import type { IDirectoryEntry } from './directory';

export interface ILicense {
    unattendedRobots?: number | string;
    taUnattendedRobots?: number | string;
    appTestRobots?: number | string;
    attendedRobots?: number | string;
    attendedConcurrentRobots?: number | string;
    studioNamedLicences?: number | string;
    nonProductionRobot?: number | string;
    studioConcurrentLicences?: number | string;
    studioXNamedLicences?: number | string;
    studioXConcurrentLicences?: number | string;
    studioProNamedLicences?: number | string;
    studioProConcurrentLicences?: number | string;
    aiFabricLicences?: number | string;
    aiGpuHelperLicences?: number | string;
    computerVision?: number | string;
}

export interface ILicenseAndKey {
    accountLicense: IAccountLicense;
    mlKeys: IMlKey[];
}

export interface IMlKey {
    licenseType: string;
    mlServiceKey: string;
    [key: string]: string;
}

export interface IAccountLicense {
    accountId: string;
    accountType: AccountType;
    products: IProduct[];
    licenseStatus: string;
    bundleCode: string;
    startDate: number;
    endDate: number;
    gracePeriod: number;
    licenseCode: string;
    subscriptionCode: string;
    subscriptionPlan: string;
    type: string;
    needsLicenseReactivation: boolean;
}

export interface IProduct {
    code: string;
    features: IFeature[];
    quantity: number;
    type: string;
}

export interface IFeature {
    code: string;
    type: string;
    value: any;
}

export interface IRobotAndServicesLicenses {
    productAllocations: IProductAllocation[];
    mlKeys?: IMlKey[];
    userLicensingBundles?: IProductAllocation[];
}

export interface IEntityAllocation {
    id: string;
    name: string;
    userBundleLicenses: string[];
    useExternalLicense: boolean;
    orphan?: boolean;
}

export interface IUserWithExplicitAllocations extends IEntityAllocation {
    email: string;
    surname: string;
    lastInUse: Date | null;
    displayName?: string;
}

export interface IGroupAllocations extends IEntityAllocation {
    name: string;
    userBundleLeases: { [key: string]: number };
}

export interface IGroupPickerAllocations extends Omit<IGroupAllocations, 'userBundleLeases' | 'userBundleLicenses' | 'useExternalLicense'> {
    groups?: IDirectoryEntry[];
    userBundleLicenses?: string[];
    useExternalLicense?: boolean;
}

export type AccountType =
    | 'COMMUNITY'
    | 'TRIAL'
    | 'ENTERPRISE'
    | 'UNLICENSED'
    | 'FREEKIT'
    | 'PRO'
    | 'PRO-TRIAL'
    | 'STD-TRIAL'
    | 'TEST-TRIAL';

export interface ILicenseCount {
    licenceType: string;
    allocated?: number | string;
    allocatedLicences: number;
    totalLicences: number;
    availableLicences: number;
}

export interface IAccountLicense {
    accountLicenseAllocationDtos: ILicenseCount[];
    accountType: AccountType;
    expiryDate: string;
    licenseStatus: string;
}

export interface IAccountProductDto {
    code: string;
    type: string;
    quantity: number;
}

export interface IAccountLicenseDto {
    accountId: string;
    bundleCode: string;
    subscriptionCode: string;
    subscriptionPlan: string;
    licenseCode: string;
    licenseStatus: string;
    products: IAccountProductDto[];
    type: string;
    parentLicenseCode?: string;
}

export interface IEntitlementOverride {
    name: string;
    enabled: boolean;
}

export interface IProductAvailability {
    code: string;
    type: string;
    availableQuantity: number;
    totalQuantity: number;
    consumedQuantity: number;
}

export interface IOrganizationLicenseWithAvailableProducts {
    license?: IAccountLicenseDto;
    productsWithAvailability: IProductAvailability[];
    entitlementOverrides: IEntitlementOverride[];
}

export interface IProductQuantity {
    code: string;
    quantity: number;
}

export interface IAllocateOrganizationLicenseRequest {
    productQuantities: IProductQuantity[];
}

export interface IOrganizationLicenseGroup {
    id: string;
    products: IOrganizationLicenseProduct[];
}

export interface IOrganizationLicenseProduct {
    code: string;
    type: string;
    quantity: number;
    available: number;
    consumedQuantity: number;
}

export interface IUserWithExplicitAllocationsPagedResult {
    totalCount: number;
    results: IUserWithExplicitAllocations[];
}

export interface IGroupAllocationsPagedResult {
    totalCount: number;
    results: IGroupAllocations[];
}

export interface IFeature {
    code: string;
    type: string;
    value: any;
}

export interface ILrmProduct {
    code: string;
    allocated: number;
    total: number;
    isConsumable: boolean;
    startDate?: number;
    endDate?: number;
    // Boon auto-sets total to 9999999 when unlimited; display the flag, not the number
    unlimited?: boolean;
}

export interface ILaProduct {
    clientId: string;
    productCode: string;
    total: number;
    used: number;
}

export interface ILicenseProduct {
    code: string;
    features: IFeature[];
    quantity: number;
    type: string;
}

export interface ILicenseDetail {
    accountId: string;
    accountType: string;
    licenseStatus: string;
    bundleCode: string;
    startDate: number;
    endDate: number;
    gracePeriod: number;
    type: string;
    subscriptionCode: string;
}

export interface IAccountLicense extends Partial<ILicenseDetail> {
    products: ILicenseProduct[];
    subscriptionPlan: string;
    licenseCode: string;
}

export interface IMlKey {
    licenseType: string;
    mlServiceKey: string;
}

export interface IApiKey {
    type: string;
    value: string;
}

export interface IProductAllocation {
    code: string;
    total: number;
    allocated: number;
    unlimited?: boolean;
}

export interface ILicensedServiceMetadata {
    id: string;
    serviceLicenseStatus: ServiceLicenseStatus;
    entitlement: string | undefined;
}

export enum ServiceLicenseStatus {
    Available = '_0',
    Used = '_1',
    NotAvailable = '_2',
}

export interface ITrial {
    subscriptionCode: string;
}

export interface IFreeTrial extends ITrial {
    isExpired: boolean;
    expiryDate: number | undefined;
}

export interface ICurrentTrial extends ITrial {
    expiryDate: number;
    products: IProductAllocation[];
    licenseCode: string;
}

export interface ITrialService {
    name: string | undefined;
    endDate: number | undefined;
}

export interface ITrialServices {
    freeTrials: IFreeTrial[];
    currentTrials: ICurrentTrial[];
}

export interface ITrialServiceDiscovery {
    eligibleTrials: ITrial[];
    currentTrials: ITrial[];
}

export interface ITabProductAllocation {
    code: string;
    licenseType?: string;
    mlServiceKey?: string;
    allocated: number;
    total: number;
    subscriptionPlan?: AccountType;
    hideProgress?: boolean;
    hideQuantity?: boolean;
    showOnlyTotal?: boolean;
    unlimited?: boolean;
}

export interface ITabConsumptionPoolUsage {
    poolName: string;
    totalQuantity: number;
    consumedQuantity: number;
    isBundled: boolean;
}

export interface ITabEntitlementUsage {
    entitlement: string;
    poolSummary: ITabConsumptionPoolUsage[];
}

export interface IConsumablePool{
    name: string;
    total: number;
    consumed: number;
    startDate?: number;
    endDate?: number;
    refreshType?: string;
    hasNextAvailable?: string;
}

export interface IConsumable {
    code: string;
    pools: IConsumablePool[];
    apiKey?: IApiKey;
}

// ── Revamp Users V2 — AI & Agentic Usage (per-user pool consumption as %) ──
export interface IUserStrongestLicense {
    // The strongest user-bundle code (e.g. "PRONU"); null when the user holds none.
    licenseType: string | null;
}

export interface IServiceConsumption {
    service: string;
    percentOfPool: number;
}

export interface IUserTopupMonthlyAggregation {
    month: string;
    topupCount: number;
}

export interface IUserAllocation {
    monthlyPercentConsumed: number;
    services: IServiceConsumption[];
    // Number of top-up grants into the user's wallet within the pool's current period.
    topups: number;
    // When the wallet first received a top-up grant.
    firstGrantedOn: string;
    // Top-up grant counts broken down per month.
    topupCountPerMonth: IUserTopupMonthlyAggregation[];
}

export interface IUserConsumable {
    entitlement: string;
    // Null when the user has no monthly entitlement pool — the entry is then wallet-only
    monthlyPercentConsumed: number | null;
    resetsOn?: string | null;
    services: IServiceConsumption[];
    // The user's personal top-up wallet for this pool (omitted when the user has none).
    userAllocation?: IUserAllocation | null;
}

export interface IUserConsumables {
    consumables: IUserConsumable[];
}

// ── Consumption pools an organization holds, with per-product breakdown - Community Revamp ──
export interface IEntitlementPool {
    entitlement: string;
    rule: string;
    percentConsumed: number;
    percentOfTotal: number;
    resetsOn?: string | null;
    services: IServiceConsumption[];
}

export interface IEntitlementPools {
    percentConsumed: number;
    resetsOn?: string | null;
    pools: IEntitlementPool[];
}
